import { Dialog } from '@/components/atoms/Dialog';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '@/components/atoms/Toast';
import { LogoText2, Rocket } from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { StripeEmbed } from './src/components/StripeEmbed';
import {
	MultiStepContent,
	MultiStepContentHandles
} from './src/components/MultistepContent';
import { RetriveUser } from './src/components/RetriveUser';
import { paywallLocalStorage } from './src/utils/paywallLocalStorage';

const PaywallEmailModalComponent = () => {
	const { toast } = useToast();

	const multiStepContentRef = useRef<MultiStepContentHandles>(null);
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		const onUpdateStorage = () => {
			const paywallEmailModal = paywallLocalStorage.getItem();

			const isModalOpen = Boolean(paywallEmailModal);

			setIsOpen(isModalOpen);
		};

		window.addEventListener('storage', onUpdateStorage);

		return () => {
			window.removeEventListener('storage', onUpdateStorage);
		};
	}, []);

	const handleCloseModal = () => {
		const currentState = paywallLocalStorage.getItem();

		if (currentState === 'payment-accepted') {
			toast({
				title: 'Payment successful. If your access hasn’t updated in a few minutes, please refresh or contact support.'
			});

			sendAnalytics({
				type: 'track',
				eventName:
					'TrialFlow - User closed the modal after the payment was completed'
			});
		} else {
			sendAnalytics({
				type: 'track',
				eventName:
					'TrialFlow - User closed the modal before the payment was completed'
			});
		}

		paywallLocalStorage.setItem('payment-cancelled');
	};

	const handleUserRoleTimeout = () => {
		toast({
			title: 'Payment successful. If your access hasn’t updated in a few minutes, please refresh or contact support.'
		});

		sendAnalytics({
			type: 'track',
			eventName:
				'TrialFlow - User role was not updated during the pooling and modal was closed'
		});

		paywallLocalStorage.setItem('user-role-timeout');
	};

	const handleRemovePaywallEmailModal = () => {
		sendAnalytics({
			type: 'track',
			eventName:
				'TrialFlow - User role was updated successfully during the pooling'
		});

		paywallLocalStorage.removeItem();
	};

	const handleGoToNextStep = () => {
		sendAnalytics({
			type: 'track',
			eventName:
				'TrialFlow - User completed the Stripe payment and went to pooling its role'
		});

		paywallLocalStorage.setItem('payment-accepted');
		multiStepContentRef.current?.handleNextStep();
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={handleCloseModal}>
			<Dialog.Content
				className="flex h-[92svh] w-[95%] max-w-[min(69rem,95%)] grow flex-row flex-col gap-0 gap-x-2 overflow-x-auto rounded-lg border-orange-brighter bg-dark bg-[url('../../../resources/assets/images/credit-card-background.svg')] p-0 sm:h-[90svh] sm:w-[80%] lg:h-[85vh] lg:w-full lg:flex-row lg:overflow-x-hidden"
				zIndex={2500}
			>
				<header className="grid grid-rows-[auto_auto_1fr] px-4 pb-4 pt-8 lg:overflow-y-auto lg:p-10">
					<LogoText2 className="mx-auto mb-7 hidden h-12 w-[10.5rem] lg:block" />

					<Dialog.Title className="flex items-center justify-center gap-3 text-pretty text-center text-xl leading-snug lg:justify-between lg:text-wrap lg:text-left lg:text-[1.35rem]">
						<Rocket className="hidden lg:block" />
						You&apos;re One Step Away from Sending Outreach That
						Works
					</Dialog.Title>

					<Dialog.Description className="mx-4 flex flex-col text-base font-normal lg:mx-0 lg:leading-relaxed">
						<p className="mb-6 mt-4 text-center lg:text-left">
							Your leads are waiting — and our AI is ready to send
							your first campaign.
						</p>

						<p className="mb-4 font-bold">
							By adding your card, you&apos;ll:
						</p>

						<ul className="mb-6 list-inside list-disc pl-2 pt-1 leading-snug">
							<li>
								Unlock AI-powered emails tailored to your target
								businesses
							</li>

							<li className="my-1.5">
								Start conversations with real, local leads —
								today
							</li>

							<li>
								Let Resquared do the heavy lifting on follow-ups
							</li>
						</ul>

						<strong className="text-center lg:text-left">
							No charge today. Cancel anytime.
						</strong>

						<p className="mt-4">
							Your card just helps us protect quality for
							everyone.
						</p>

						<div className="mt-11 hidden h-full min-h-28 bg-[url('../../../resources/assets/images/credit-card-image.svg')] bg-contain bg-center bg-no-repeat lg:block" />
					</Dialog.Description>
				</header>

				<div className="p-4 lg:px-8 lg:pb-8 lg:pt-5">
					<MultiStepContent ref={multiStepContentRef}>
						<StripeEmbed onComplete={handleGoToNextStep} />
						<RetriveUser
							onUserRoleUpdated={handleRemovePaywallEmailModal}
							onUserRoleTimout={handleUserRoleTimeout}
						/>
					</MultiStepContent>

					<div className="mb-5 mt-6 aspect-[313/311] max-h-40 w-full bg-[url('../../../resources/assets/images/credit-card-image.svg')] bg-contain bg-center bg-no-repeat lg:hidden" />
					<LogoText2 className="mx-auto mb-7 block h-12 w-[10.5rem] lg:hidden" />
				</div>
			</Dialog.Content>
		</Dialog.Root>
	);
};

export const PaywallEmailModal = () =>
	createPortal(<PaywallEmailModalComponent />, document.body);
